import { i18n, Fishx, setLang as setLocale } from 'fishx';
import { message } from '@whalecloud/fdx';
import {
  logged,
  queryLocal,
  queryLangs,
  current,
  queryUserPortals,
  querySysparams,
  queryAdmin,
  queryCurrent,
  addComponentPrivilege,
  checkComponentPrivilege,
  getMenuComponentPrivs,
  delComponentPrivs,
  getMagicCode,
  qryCurrentSecurityRule,
  queryUserRoles,
  addBrowserLog,
  getFlowList,
  initProcess,
  startProcess,
  componentFlowBind,
  getComponentFlow,
  componentFlowUpdate,
  orgJobsEnabled,
  getOrgJobs,
  currentStaffJob,
  staffCurrent,
} from '@/services/api';
import appGlobal from '@/utils/appGlobal';
import loginSession from '@/utils/loginSession';
import { Reducer, Effect } from '@/typings/connect.d';
import { SecurityRuleType, LanguageType } from '@/typings/data.d';
import {
  DRM_VERIFY_CODE_EXPIRE_TIME,
  DRM_VERIFY_CODE_RESEND_TIME,
  DRM_DEFAULT_ACCNBR_PREFIX,
  DRM_ETC_BALANCE_UNIT,
  DRM_DEF_CURRENCY_PRECISION,
  DRM_CURRENCY_SHOW_PRECISION,
  DRM_APP_DOWNLOAD_URL,
  DRM_BSNL_POI_POA_RULE,
  DRM_CLOUD_DISK_PATH
} from '@/utils';
import { transformFormat, setCookie, langMap, getBaseConfig } from '@/utils/utils';

export interface GlobalStateType {
  collapsed?: any;
  currentUser: {
    menuShowType?: string;
    userId?: string | number;
  };
  languages?: LanguageType[];
  currentUserRole?: [];
  menuComponentPrivs?: [];
  loadInitial?: boolean; // 子系统渲染状态
  minWidthState?: boolean;
  sysparams?: []; // 系统参数
  securityRule?: SecurityRuleType; // 安全规则
  pageShow?: string; // 控制菜单界面的展示还是隐藏
  defaultUrl?: '/';
  loginPage: string | null; // 远程登录页url，空字符串则使用本地
  auditLogFilterKeys?: [];
  auditLogFilterValues?: [];
}

export interface GlobalModelType {
  namespace: string;
  state: GlobalStateType;
  effects: {
    fetchLocal: Effect;
    fetchLangs: Effect;
    setCurrentMenu: Effect;
    fetchCurrentPortal: Effect;
    fetchSys: Effect;
    fetchAdmin: Effect;
    fetchCurrentAdmin: Effect;
    logged: Effect;
    fetchCurrentUserRole: Effect;
    addComponentPrivilege: Effect;
    checkComponentPrivilege: Effect;
    getMenuComponentPrivs: Effect;
    delComponentPrivs: Effect;
    getMagicCode: Effect;
    fetchSecurityRule: Effect;
    getFlowList: Effect;
    initProcess: Effect;
    startProcess: Effect;
    componentFlowBind: Effect;
    getComponentFlow: Effect;
    componentFlowUpdate: Effect;
  };
  // @ts-ignore
  reducers: {
    changeLayoutCollapsed: Reducer<Partial<GlobalStateType>>;
    updateState: Reducer<GlobalStateType>;
  };
}

const Model: GlobalModelType = {
  namespace: 'dxpGlobal',

  state: {
    collapsed: loginSession.getData('collapsed'),
    currentUser: {},
    languages: [],
    currentUserRole: [],
    menuComponentPrivs: [],
    loadInitial: false,
    minWidthState: true,
    sysparams: [], // 系统参数
    securityRule: {}, // 安全规则
    pageShow: 'block', // 控制菜单界面的展示还是隐藏
    defaultUrl: '/',
    loginPage: null,
    auditLogFilterKeys: [],
    auditLogFilterValues: [],
  },

  effects: {
    *fetchLocal(_, { call, put }) {
      const { success, data } = yield call(queryLocal);
      if (success) {
        const {
          // shortLanguage = '',
          // language = '',
          auditLogEventCode = '',
          auditLogEventCodeRes = '',
        } = data;
        setCookie('ZSMART_LOCALE', 'en', 30);
        setCookie('userLocale', 'en-US', 30);
        setLocale(langMap.en);
        const auditLogFilterKeys = auditLogEventCode.split('|');
        const auditLogFilterValues = auditLogEventCodeRes.split('|');
        yield put({
          type: 'updateState',
          payload: {
            auditLogFilterKeys,
            auditLogFilterValues,
          },
        });
      }
    },
    *fetchLangs(_, { call, put }) {
      const { success, data } = yield call(queryLangs);
      if (success) {
        const { languageList } = data;
        yield put({
          type: 'updateState',
          payload: {
            languages: languageList,
          },
        });
      }
    },
    *setCurrentMenu(_, { call }) {
      yield call(current, { menuId: -2 });
    },
    *fetchCurrentPortal({ payload }, { call }) {
      yield call(queryUserPortals, payload);
    },
    *fetchSys(_, { call, put }) {
      const { success, data } = yield call(querySysparams);
      if (success) {
        const sysparams = {
          timeFormat: transformFormat(data[4]),
          dateFormat: transformFormat(data[3]),
          dataTimeFormat: transformFormat(data[2]),
        };
        yield put({
          type: 'updateState',
          payload: {
            sysparams,
          },
        });
      }
    },
    *fetchAdmin(_, { call }) {
      yield call(queryAdmin);
    },
    *fetchCurrentAdmin(_, { call, put, select }) {
      const { success, data } = yield call(queryCurrent);
      if (success) {
        const { currentUser } = yield select((state: any) => state.dxpGlobal);
        const newCurrent = {
          ...currentUser,
          ...data,
        };
        yield put({
          type: 'updateState',
          payload: {
            currentUser: newCurrent,
          },
        });
      }
    },
    *logged({ payload = {} }, { call, put }) {
      const { success, data } = yield call(logged);
      if (success) {
        // 确定当前登录页url
        yield put({
          type: 'updateState',
          payload: {
            loginPage: data.loginPage,
          },
        });
        if (data.userId) {
          loginSession.saveUser(data);
          yield put({
            type: 'updateState',
            payload: {
              currentUser: {
                ...data,
              },
            },
          });
          appGlobal.setAppGlobal(data);
          const taskArr = [
            put({
              type: 'business/qryConfigParams',
              payload: {
                paramCodes: `${DRM_VERIFY_CODE_EXPIRE_TIME},${DRM_VERIFY_CODE_RESEND_TIME},${DRM_DEFAULT_ACCNBR_PREFIX},${DRM_ETC_BALANCE_UNIT},${DRM_DEF_CURRENCY_PRECISION},${DRM_CURRENCY_SHOW_PRECISION},${DRM_APP_DOWNLOAD_URL},${DRM_BSNL_POI_POA_RULE},${DRM_CLOUD_DISK_PATH}`,
              },
            }),
            put({
              type: 'search/fetchLoginInfo',
              payload: {
                ...payload,
              },
            }),
            put({
              type: 'dxpUser/fetchMenu',
              payload: {
                ...payload,
              },
            }),
            put({ type: 'fetchSecurityRule' }),
            put({ type: 'setCurrentMenu' }),
            put({ type: 'fetchSys' }),
            put({
              type: 'fetchCurrentUserRole',
              payload: data.userId,
            }),
            put({ type: 'dxpUser/fetchPortals' }),
            put({ type: 'dxpUser/fetchCurrentUserSpList' }),
          ];
          // 记录浏览器信息
          if (data.recordBrowser === 'true') {
            const browserInfo = getBaseConfig();
            (browserInfo as any).comments = JSON.stringify(browserInfo);
            yield call(addBrowserLog, browserInfo);
          }
          // staffCurrent
          yield call(staffCurrent);
          // 需要先执行 staffJob
          const { success: orgJobsEnabledSuccess, data: orgJobsEnabledData } = yield call(orgJobsEnabled);
          if (orgJobsEnabledSuccess && orgJobsEnabledData === 'true') {
            // 先执行staffJob
            const { success: orgJobsdataSuccess, data: orgJobsList = [] } = yield call(getOrgJobs);
            if (orgJobsdataSuccess) {
              if (orgJobsList && orgJobsList.length > 0) {
                // 默认取第一个 staffJobId
                const [{ staffJobId }] = orgJobsList;
                setCookie('staffJobId', staffJobId, 30);
                yield call(currentStaffJob, { staffJobId });
              }
            }
          }
          yield taskArr;
        } else if (window.location.pathname.indexOf('/userLogin') <= -1) {
          Fishx.history.push('/userLogin');
        }
      }
    },
    *fetchCurrentUserRole({ payload }, { call, put }) {
      const { success, data } = yield call(queryUserRoles, payload);
      if (success) {
        yield put({
          type: 'updateState',
          payload: {
            currentUserRole: data,
          },
        });
      }
    },
    *addComponentPrivilege({ payload }, { call }) {
      const { method, ...resetProps } = payload;
      const { success, data } = yield call(addComponentPrivilege, method, resetProps);
      if (success) {
        if (data === 'success') {
          message.success(i18n('COMPONENT_PRIVILEGE_EDIT_SUCCESS'));
        } else {
          message.success(i18n('COMPONENT_PRIVILEGE_ADD_SUCCESS'));
        }
      }
    },
    *checkComponentPrivilege({ payload }, { call }) {
      const { success, data } = yield call(checkComponentPrivilege, payload);
      if (success) {
        return data || '';
      }
      return null;
    },
    *getMenuComponentPrivs({ payload }, { call, put }) {
      const { success, data } = yield call(getMenuComponentPrivs, payload);
      if (success) {
        yield put({
          type: 'updateState',
          payload: {
            menuComponentPrivs: data,
          },
        });
      }
    },
    *delComponentPrivs({ payload }, { call }) {
      const { success, data } = yield call(delComponentPrivs, payload);
      if (success) {
        if (data === 'success') {
          message.success(i18n('COMPONENT_PRIVILEGE_DELETE_SUCCESS'));
        }
      }
    },

    *getMagicCode({ payload }, { call }) {
      // TODO 这里是IframeModule里面打开iframe菜单调用的
      // const { currentUser } = yield select((state: { dxpGlobal: any }) => state.dxpGlobal);
      // payload.password = currentUser.password;
      const { success, data } = yield call(getMagicCode, payload);
      if (success) {
        return data;
      }
      return null;
    },
    *fetchSecurityRule(_, { call, put }) {
      const { success, data } = yield call(qryCurrentSecurityRule);
      if (success) {
        yield put({
          type: 'updateState',
          payload: {
            securityRule: data,
          },
        });
      }
    },
    // 获取流程
    *getFlowList(_: any, { call }: any) {
      const pagination = {
        pageSize: 10000,
        pageNum: 1,
        orderFields: 'CREATE_DATE DESC',
      };
      const { success, data } = yield call(getFlowList, pagination);
      if (success) {
        return data;
      }
      return null;
    },
    // 初始化流程
    *initProcess({ payload }: any, { call }: any) {
      const { success, data } = yield call(initProcess, { ...payload });
      if (success) {
        return data;
      }
      return null;
    },
    // 启动流程
    *startProcess({ payload }: any, { call }: any) {
      const params = { processDefKey: payload.processDefKey, formJsonValue: JSON.stringify(payload.formJsonValue) };
      const { success, data } = yield call(startProcess, JSON.stringify({ ...params }));
      if (success) {
        message.info('启动成功');
      }
      return data;
    },
    // 按钮流程绑定
    *componentFlowBind({ payload }: any, { call }: any) {
      const { success, data } = yield call(componentFlowBind, { ...payload });
      if (success) {
        return data;
      }
      return null;
    },
    // 按钮流程查询
    *getComponentFlow({ payload }: any, { call }: any) {
      const { success, data } = yield call(getComponentFlow, { ...payload });
      if (success) {
        return data;
      }
      return null;
    },
    // 按钮流程更新
    *componentFlowUpdate({ payload }: any, { call }: any) {
      const { success, data } = yield call(componentFlowUpdate, { ...payload });
      if (success) {
        return data;
      }
      return null;
    },
  },

  reducers: {
    changeLayoutCollapsed(state, { payload }) {
      return {
        ...state,
        collapsed: payload,
      };
    },
    updateState(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
  },
};

export default Model;
